import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table, Tag, Spin, Empty, Button, Select, Space, message, DatePicker, Tooltip,
} from "antd";
import {
  CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, SyncOutlined, RightOutlined, DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../../const";
import ComplianceDocViewer from "../../../components/ComplianceDocViewer/ComplianceDocViewer";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PageWrapper = styled.div`
  h1 { font-size: 24px; font-weight: 600; color: #014c75; margin: 0 0 4px 0; }
  p.subtitle { color: #666; font-size: 14px; margin: 0 0 24px 0; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  background: #f0f0f0;
  cursor: pointer;
  font-size: 10px;
  color: #666;
  margin-right: 8px;
  transition: background 0.2s;
  &:hover { background: #d9d9d9; }
`;

const DOC_TYPE_LABELS = { nda: "NDA", contract: "Contract Agreement", declaration: "Talent Declaration" };
const DOC_TYPE_COLORS = { nda: "blue", contract: "purple", declaration: "cyan" };
const DOC_TYPES = ["nda", "contract", "declaration"];

const AdminComplianceDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [docTypeFilter, setDocTypeFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (statusFilter) body.status = statusFilter;
      if (docTypeFilter) body.docType = docTypeFilter;
      if (dateRange?.[0]) body.startDate = dateRange[0].toISOString();
      if (dateRange?.[1]) body.endDate = dateRange[1].toISOString();
      const res = await axios.post(API_CONST.COMPLIANCE_DOCUMENTS_LIST, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(res.data.documents || []);
    } catch (err) {
      message.error("Failed to load compliance documents");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, docTypeFilter, dateRange]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Group flat documents into per-hiring-record rows with child doc arrays
  const groupedRows = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (!map.has(doc.hiringRecordId)) {
        map.set(doc.hiringRecordId, {
          key: doc.hiringRecordId,
          hiringRecordId: doc.hiringRecordId,
          talentName: doc.talentName || "—",
          partnerOrganization: doc.partnerOrganization || "—",
          customerOrganization: doc.customerOrganization || "—",
          docs: {},
        });
      }
      map.get(doc.hiringRecordId).docs[doc.docType] = doc;
    });
    return Array.from(map.values());
  }, [documents]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(API_CONST.COMPLIANCE_BACKFILL_ALL, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(res.data.message || "Sync complete");
      fetchDocuments();
    } catch (err) {
      message.error(err.response?.data?.error || "Sync failed");
    } finally {
      setBackfilling(false);
    }
  };

  const openView = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_CONST.COMPLIANCE_DOCUMENTS}/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewDoc(res.data.document);
    } catch (err) {
      message.error("Failed to load document");
    }
  };

  const toggleRow = (hiringRecordId) => {
    setExpandedRows((prev) =>
      prev.includes(hiringRecordId)
        ? prev.filter((id) => id !== hiringRecordId)
        : [...prev, hiringRecordId]
    );
  };

  // Parent (accordion) columns
  const parentColumns = [
    {
      title: "Hiring ID",
      dataIndex: "hiringRecordId",
      key: "hiringRecordId",
      width: 190,
      render: (id, row) => {
        const expanded = expandedRows.includes(id);
        return (
          <span style={{ display: "flex", alignItems: "center" }}>
            <ExpandIcon onClick={() => toggleRow(id)}>
              {expanded ? <DownOutlined /> : <RightOutlined />}
            </ExpandIcon>
            <span style={{ color: "#014c75", fontWeight: 500 }}>{id}</span>
          </span>
        );
      },
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 160,
    },
    {
      title: "Partner Org",
      dataIndex: "partnerOrganization",
      key: "partnerOrg",
      width: 170,
      ellipsis: true,
    },
    {
      title: "Customer Org",
      dataIndex: "customerOrganization",
      key: "customerOrg",
      width: 170,
      ellipsis: true,
    },
    {
      title: "Documents",
      key: "docCount",
      width: 120,
      render: (_, row) => {
        const total = DOC_TYPES.filter((t) => row.docs[t]).length;
        const signed = DOC_TYPES.filter((t) => row.docs[t]?.status === "signed").length;
        return (
          <span style={{ color: "#666", fontSize: 13 }}>
            {signed}/{total} signed
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, row) => {
        const allSigned = DOC_TYPES.every((t) => !row.docs[t] || row.docs[t]?.status === "signed");
        const hasDocs = DOC_TYPES.some((t) => row.docs[t]);
        if (!hasDocs) return <Tag color="default">—</Tag>;
        return allSigned ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Signed</Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
        );
      },
    },
  ];

  // Child rows rendered inside expandable section
  const renderExpandedRow = (row) => {
    const childData = DOC_TYPES
      .filter((t) => row.docs[t])
      .map((t) => row.docs[t]);

    return (
      <Table
        size="small"
        showHeader={false}
        dataSource={childData}
        rowKey="id"
        pagination={false}
        style={{ marginLeft: 32, marginBottom: 4 }}
        columns={[
          {
            key: "docType",
            width: 190,
            render: (_, doc) => (
              <Tag color={DOC_TYPE_COLORS[doc.docType]}>
                {DOC_TYPE_LABELS[doc.docType] || doc.docType}
              </Tag>
            ),
          },
          { key: "talent", width: 160, render: () => null },
          { key: "partner", width: 170, render: () => null },
          { key: "customer", width: 170, render: () => null },
          {
            key: "docCountSpacer",
            width: 120,
            render: (_, doc) =>
              doc.signedDate ? (
                <span style={{ color: "#888", fontSize: 12 }}>
                  {new Date(doc.signedDate).toLocaleDateString("en-GB")}
                </span>
              ) : (
                <span style={{ color: "#bbb", fontSize: 12 }}>Not signed</span>
              ),
          },
          {
            key: "status",
            width: 130,
            render: (_, doc) =>
              doc.status === "signed" ? (
                <Tag icon={<CheckCircleOutlined />} color="success">Signed</Tag>
              ) : (
                <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>
              ),
          },
          {
            key: "action",
            render: (_, doc) => (
              <Button size="small" icon={<EyeOutlined />} onClick={() => openView(doc)}>
                View
              </Button>
            ),
          },
        ]}
      />
    );
  };

  return (
    <PageWrapper>
      <h1>Compliance Documents</h1>
      <p className="subtitle">
        Platform-wide view of all NDA, Contract, and Talent Declaration documents
      </p>

      <FilterBar>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 140 }}
          value={statusFilter}
          onChange={setStatusFilter}
        >
          <Option value="pending">Pending</Option>
          <Option value="signed">Signed</Option>
        </Select>
        <Select
          allowClear
          placeholder="Document type"
          style={{ width: 180 }}
          value={docTypeFilter}
          onChange={setDocTypeFilter}
        >
          <Option value="nda">NDA</Option>
          <Option value="contract">Contract</Option>
          <Option value="declaration">Declaration</Option>
        </Select>
        <RangePicker
          onChange={(dates) => setDateRange(dates ? [dates[0].toDate(), dates[1].toDate()] : null)}
          placeholder={["Created from", "Created to"]}
          style={{ width: 260 }}
        />
        <Button onClick={fetchDocuments}>Refresh</Button>
        <Tooltip title="Auto-generate any missing compliance docs for all active hiring records">
          <Button
            icon={<SyncOutlined spin={backfilling} />}
            loading={backfilling}
            onClick={handleBackfill}
          >
            Sync Missing Docs
          </Button>
        </Tooltip>
      </FilterBar>

      <Spin spinning={loading}>
        {groupedRows.length > 0 ? (
          <>
            <Table
              columns={parentColumns}
              dataSource={groupedRows}
              rowKey="hiringRecordId"
              expandable={{
                expandedRowKeys: expandedRows,
                expandedRowRender: renderExpandedRow,
                showExpandColumn: false,
              }}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showTotal: (total) => `${total} hiring records`,
              }}
              scroll={{ x: 1000 }}
              rowClassName={(row) =>
                expandedRows.includes(row.hiringRecordId) ? "row-expanded" : ""
              }
            />
          </>
        ) : (
          <Empty
            description={
              loading ? "Loading..." : (
                <span>
                  No compliance documents found.{" "}
                  Documents are created automatically when a hiring record is established.
                  Use <strong>Sync Missing Docs</strong> if records exist but documents are absent.
                </span>
              )
            }
          />
        )}
      </Spin>

      {viewDoc && (
        <ComplianceDocViewer
          document={viewDoc}
          open={!!viewDoc}
          onClose={() => setViewDoc(null)}
          mode="drawer"
        />
      )}
    </PageWrapper>
  );
};

export default AdminComplianceDocuments;
